import { Box, Typography, Stack, Badge } from '@mui/material';
import { motion } from 'motion/react';
import ShortLogo from 'assets/media/images/pagii-chatshop-logo-trimmed.svg';
import 'assets/css/adminlte.css';
import React, { useEffect, useState } from 'react';

import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar';
import features from 'constants/MenuItem/features';
import { Link, useLocation } from 'react-router-dom';
import useRedux from 'redux/useRedux';

import { fetchConfigurationData } from 'redux/merchant';
import { useOrderStatusCounters } from 'hooks/useOrderStatusCounters';
// import { useFeatureFlags } from 'context/feature-flag.context';
import MulaiTourButton from 'components/Onboarding/MulaiTourButton';
import { useOnboardingSafe } from 'context/onboarding.context';
import { onboardingSteps } from 'constants/onboarding.steps';

interface SidebarProps {
  sidebar: string;
}

// Mapping: feature/child id → data-tour attribute value
const TOUR_ID_MAP: Record<string, string> = {
  dashboard: 'nav-dashboard',
  customers: 'nav-customers',
  resellerlist: 'nav-resellerlist',
  listproducts: 'nav-listproducts',
  productvariants: 'nav-productvariants',
  category: 'nav-category',
  brands: 'nav-brands',
  aistudio: 'nav-aistudio',
  pendingorder: 'nav-pendingorder',
  neworder: 'nav-neworder',
  processorder: 'nav-processorder',
  readypickuporder: 'nav-readypickuporder',
  deliveryorder: 'nav-deliveryorder',
  complainedorder: 'nav-complainedorder',
  waitingconfirmationorder: 'nav-waitingconfirmationorder',
  completedorder: 'nav-completedorder',
  canceledorder: 'nav-canceledorder',
  bank: 'nav-bank',
  withdraw: 'nav-withdraw',
  reportorder: 'nav-reportorder',
  reporttransaction: 'nav-reporttransaction',
  settingprofil: 'nav-settingprofil',
  settingbusinessday: 'nav-settingbusinessday',
  settingdelivery: 'nav-settingdelivery',
  settingchatbot: 'nav-settingchatbot',
  'chat-history': 'nav-chat-history',
  promo: 'nav-promo',
};

export default function SideBar({ sidebar }: SidebarProps) {
  const {
    storeState: { Auth },
    thunkDispatch,
  } = useRedux();
  const merchant = Auth?.data?.data?.merchant ?? '';
  const userRole = Auth?.data?.data?.roles?.[0] ?? '';
  const location = useLocation();

  const [configuration, setConfiguration] = useState({
    penjualanRetail: false,
    penjualanJasa: false,
    promoEnabled: false
  });

  const { badgeCounts } = useOrderStatusCounters();
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderTouched, setOrderTouched] = useState(false);

  // Onboarding: which parent submenu should be force-expanded on current step
  const onboarding = useOnboardingSafe();
  const tourExpandMenu = onboarding?.isActive
    ? (onboardingSteps[onboarding.currentStep]?.expandMenu ?? null)
    : null;

  useEffect(() => {
    const token = Auth.data.data.token;
    const merchant = Auth.data.data.merchant;
    const promoEnabled = Auth.data.data.promoEnabled;

    if (merchant && merchant.id) {
      const idBusiness = merchant.id;

      thunkDispatch(fetchConfigurationData({ idBusiness, token }))
        .unwrap()
        .then((res) => {
          if (res) {
            // Assign fetched configuration values to state
            const retail = res.data?.penjualanRetail;
            const jasa = res.data?.penjualanJasa;

            setConfiguration({
              penjualanRetail: (!retail && !jasa) ? true : !!retail,
              penjualanJasa: !!jasa,
              promoEnabled
            });
          }
        })
        .catch((error) => console.error('Failed to fetch configuration:', error));
    } else {
      console.warn('Merchant ID is not available for Super Admin');

      setConfiguration({
        penjualanRetail: false,
        penjualanJasa: false,
        promoEnabled: false
      });
    }
  }, [thunkDispatch, Auth]);

  const filteredFeatures = features.filter(
    feature => userRole === 'ROLE_SUPER_ADMIN' || feature.id !== 'adminrevenue'
  ).filter(
    feature => feature.id !== 'promo'
  )
    .map((feature) => {
      if (feature.children && Array.isArray(feature.children)) {
        let filteredChildren = feature.children;
        if (userRole === 'ROLE_ADMIN') {
          filteredChildren = feature.children.filter((child) => child.id !== 'settingchatbot');
        }
        if (userRole !== 'ROLE_SUPER_ADMIN') {
          filteredChildren = filteredChildren.filter((child) => child.id !== 'settingchatbot' && child.id !== 'settingpayout' && child.id !== 'settingaffiliatefee' && child.id !== 'settingadminpayouts');
        }
        if (userRole === 'ROLE_SUPER_ADMIN') {
          filteredChildren = filteredChildren.filter(
            (child) => child.id !== 'settingprofil' && child.id !== 'settingdelivery' && child.id !== 'settingbusinessday',
          );
        }

        if (feature.id === 'product') {
          filteredChildren = filteredChildren.filter((child) => {
            if (child.id === 'aistudio' && child.target === '_blank') {
              return true;
            }
            if (child.id === 'brands' && !configuration.penjualanRetail) {
              return false;
            }
            if (child.id === 'provider' && !configuration.penjualanJasa) {
              return false;
            }
            return true;
          });
        }

        return { ...feature, children: filteredChildren };
      }
      return feature;
    })
    .filter((feature) => {
      if (userRole === 'ROLE_SUPER_ADMIN') {
        return ![
          'customers',
          'product',
          'order',
          'finance',
          'report',
          'reseller',
          'order-service',
          'custom',
          'chat-history',
          'promo'
        ].includes(feature.id);
      }
      if (userRole === 'ROLE_ADMIN') {
        const isRetail = Boolean(configuration.penjualanRetail);
        const isJasa = Boolean(configuration.penjualanJasa);

        const excluded = ['users', 'merchant', 'custom'];

        if (!isRetail) {
          excluded.push('order');
        }
        if (!isJasa) {
          excluded.push('order-service');
        }

        return !excluded.includes(feature.id);
      }
      return true;
    });

  const getOrderBadgeByChildId = (id: string): number => {
    if (!badgeCounts) return 0;

    switch (id) {
      case 'pendingorder':
        return badgeCounts[0] || 0;
      case 'neworder':
        return badgeCounts[1] || 0;
      case 'processorder':
        return badgeCounts[2] || 0;
      case 'readypickuporder':
        return badgeCounts[3] || 0;
      case 'deliveryorder':
        return badgeCounts[4] || 0;
      case 'complainedorder':
        return badgeCounts[5] || 0;
      case 'waitingconfirmationorder':
        return badgeCounts[6] || 0;
      case 'completedorder':
        return 0;
      case 'canceledorder':
        return 0;
      default:
        return 0;
    }
  };

  const retailTotalBadge =
    (badgeCounts[0] || 0) +
    (badgeCounts[1] || 0) +
    (badgeCounts[2] || 0) +
    (badgeCounts[3] || 0) +
    (badgeCounts[4] || 0) +
    (badgeCounts[5] || 0) +
    (badgeCounts[6] || 0);
  // (badgeCounts[7] || 0) +
  // (badgeCounts[8] || 0);

  const SidebarBadge = ({ count, children, anchorOrigin }: { count: number; children: React.ReactNode; anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' } }) => {
    if (!count || count <= 0) return <>{children}</>;

    return (
      <Badge
        badgeContent={count}
        max={99}
        color="error"
        anchorOrigin={anchorOrigin ?? { vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: '#f44336',
            color: 'white',
            border: '2px solid white',
          },
        }}
      >
        {children}
      </Badge>
    );
  };

  const renderBadge = (value: number) => {
    if (!value || value <= 0) return null;

    return (
      <Badge
        badgeContent={value}
        max={99}
        sx={{
          marginLeft: '8px',
          marginRight: '14px',
          '& .MuiBadge-badge': {
            backgroundColor: '#f44336',
            color: 'white',
            fontSize: '10px',
            fontWeight: 'bold',
            height: '20px',
            minWidth: '20px',
            position: 'static',
            transform: 'none',
            borderRadius: '15px'
          },
        }}
      />
    );
  };

  const isActiveMenuItem = (route?: string, path?: string) => {
    if (!route || route === '#') {
      return path === '/' ? location.pathname === '/' : false;
    };
    if (route === '/') return location.pathname === '/';
    return location.pathname.startsWith(route);
  };

  // Helper function to check if any child is active (for parent menu highlighting)
  const hasActiveChild = (children: any[]) => {
    return children.some((child) => isActiveMenuItem(child.route, child.path) && child.target !== '_blank');
  };

  // Inisialisasi state open Penjualan Retail berdasar route aktif,
  // tapi jangan override setelah user interaksi manual (orderTouched = true)
  useEffect(() => {
    if (orderTouched) return;

    const orderFeature = filteredFeatures.find((f) => f.id === 'order' && f.children);
    if (orderFeature && orderFeature.children) {
      const anyActive = hasActiveChild(orderFeature.children);
      setOrderOpen(anyActive);
    }
  }, [location.pathname, filteredFeatures, orderTouched]);

  const renderIcon = (icon: any, text: string, isActive: boolean = false, badgeCount: number = 0) => {
    const activeColor = '#2ec291';
    const normalColor = '#333333';

    let IconContent = null;

    if (typeof icon === 'string') {
      IconContent = (
        <img
          src={icon}
          width={25}
          height={25}
          alt={text + '-icon'}
          style={{
            filter: isActive ? 'invert(66%) sepia(51%) saturate(543%) hue-rotate(116deg) brightness(94%) contrast(92%)' : 'none'
          }}
        />
      );
    } else if (icon) {
      const IconComponent = icon;
      IconContent = (
        <IconComponent sx={{
          fontSize: 25,
          color: isActive ? activeColor : normalColor,
          stroke: isActive ? activeColor : 'none',
          strokeWidth: isActive ? 0.5 : 0
        }} />
      );
    } else {
      // Placeholder for submenus/items without icons that have badges
      IconContent = badgeCount > 0 ? (
        <Box sx={{ width: 12, height: 12 }} />
      ) : null;
    }

    return (
      <SidebarBadge count={badgeCount}>
        {IconContent}
      </SidebarBadge>
    );
  };

  return (
    <aside className="fixed top-0 left-0 h-full w-auto flex flex-col justify-start items-center transition-all duration-400 ease-in border-r border-[#dee2e6] overflow-x-hidden overflow-y-hidden z-[10000] bg-white">
      <Sidebar
        collapsed={sidebar.toString() === 'false'}
        transitionDuration={700}
        rootStyles={{
          borderRight: 'none',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div
          className={`float-left overflow-x-hidden bg-white z-[10000] transition-[width] duration-400 ease-in ${sidebar === 'true' ? 'w-[280px]' : 'w-[5rem]'
            }`}
        >
          <header
            className={`w-full h-[60px] border-b border-[#dee2e6] flex items-center transition-all duration-400 ease-in ${sidebar === 'true' ? 'px-4' : 'justify-center'}`}
          >
            <div className={`flex items-center gap-3 ${sidebar === 'true' ? 'w-full' : ''}`}>
              <motion.img
                data-tour="nav-merchant-profile"
                className="w-[40px] h-[40px] rounded-full object-cover elevation-1"
                src={merchant && merchant.foto ? merchant.foto : ShortLogo}
                alt="merchant-photo"
                transition={{ type: "spring", stiffness: 200, damping: 25 }}
              />
              {sidebar === 'true' && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex-1 overflow-hidden"
                >
                  <Typography variant="body2" fontWeight="500" sx={{ color: '#1e293b', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {merchant.businessName}
                  </Typography>
                </motion.div>
              )}
            </div>
          </header>
        </div>

        <Menu
          rootStyles={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            background: '#ffffff',
            borderRight: 'none',
            paddingBottom: '8px',
          }}
        >
          {filteredFeatures.map((feature) => {
            if (feature && feature.children) {
              const isParentActive = isActiveMenuItem(feature.route, feature.path) || hasActiveChild(feature.children);
              const isOrderMenu = feature.id === 'order';

              // Handle SubMenu visibility:
              // - If tour is ACTIVE: ONLY open the specific menu targeted by the current tour step.
              // - If tour is INACTIVE: Open the menu if the user's current route is inside it (isParentActive).
              // Since react-pro-sidebar uses `defaultOpen` for initial state, we must change the `key`
              // whenever this `shouldOpen` condition changes to force React to remount the SubMenu.
              const isTourActive = Boolean(onboarding?.isActive);
              const isTourForcedOpen = tourExpandMenu === feature.id;

              const shouldOpen = isTourActive ? isTourForcedOpen : isParentActive;
              const subMenuKey = `${feature.text}-open-${shouldOpen}`;

              return (
                <SubMenu
                  label={
                    <motion.div
                      className="flex w-full items-center gap-2 align-middle"
                      whileHover={{ x: 3 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                      <span style={{ fontWeight: 500, color: isParentActive ? '#2ec291' : 'inherit', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{feature.text}</span>
                    </motion.div>
                  }
                  icon={renderIcon(feature.icon, feature.text, isParentActive, (isOrderMenu && !orderOpen) ? retailTotalBadge : 0)}
                  key={subMenuKey}
                  defaultOpen={shouldOpen}
                  onOpenChange={(open) => {
                    if (isOrderMenu) {
                      setOrderOpen(open);
                      setOrderTouched(true);
                    }
                  }}
                  rootStyles={{
                    ['& > .' + menuClasses.button]: {
                      backgroundColor: isParentActive ? '#f0fdf4' : '#ffffff',
                      color: isParentActive ? '#2ec291' : '#333333',
                      '&:hover': {
                        backgroundColor: isParentActive ? '#dcfce7' : '#ededed',
                      },
                    },
                    ['.' + menuClasses.subMenuContent]: {
                      backgroundColor: '#ffffff',
                    },
                  }}
                >
                  {feature.children.map((item) => {
                    const isActive = isActiveMenuItem(item.route, item.path);
                    const itemBadge = isOrderMenu ? getOrderBadgeByChildId(item.id) : 0;
                    const isExternal = item.target === '_blank';
                    const MenuLinkComponent = isExternal ? (
                      <a href={item.path} target="_blank" style={{ textDecoration: 'none' }} />
                    ) : (
                      <Link to={item.path} />
                    );

                    // Feature flag
                    // if (item.flag && flags[item.flag] === false) {
                    //   return null;
                    // }

                    const tourId = TOUR_ID_MAP[item.id];

                    return (
                      <MenuItem
                        key={item.text + '-icon'}
                        component={MenuLinkComponent}
                        rootStyles={{
                          ['.' + menuClasses.button]: {
                            backgroundColor: isActive ? '#f0fdf4' : 'transparent',
                            color: isActive ? '#2ec291' : '#333333',
                            overflow: 'visible',
                            '&:hover': {
                              backgroundColor: isActive ? '#dcfce7' : '#f5f5f5',
                            },
                          },
                        }}
                      >
                        <motion.div
                          className="flex items-center w-full"
                          style={{ paddingTop: '8px' }}
                          whileHover={{ x: 5 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                          {...(tourId ? { 'data-tour': tourId } : {})}
                        >
                          <span style={{ fontWeight: 500, color: isActive ? '#2ec291' : 'inherit', whiteSpace: 'nowrap', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.text}</span>
                          {renderBadge(itemBadge)}
                        </motion.div>
                      </MenuItem>
                    );
                  })}
                </SubMenu>
              );
            } else {
              // Handle single menu items (without children)
              const isActive = isActiveMenuItem(feature.route, feature.path);
              const isExternal = feature.target === '_blank';
              const MenuLinkComponent = isExternal ? (
                <a href={feature.path} target="_blank" style={{ textDecoration: 'none' }} />
              ) : (
                <Link to={feature.path} />
              );

              const tourId = TOUR_ID_MAP[feature.id];

              return (
                <MenuItem
                  key={feature.text + '-icon'}
                  component={MenuLinkComponent}
                  icon={renderIcon(feature.icon, feature.text, isActive)}
                  rootStyles={{
                    ['.' + menuClasses.button]: {
                      backgroundColor: isActive ? '#f0fdf4' : 'transparent',
                      color: isActive ? '#2ec291' : '#333333',
                      '&:hover': {
                        backgroundColor: isActive ? '#dcfce7' : '#f5f5f5',
                      },
                    },
                  }}
                >
                  <motion.div
                    className="flex w-full items-center"
                    whileHover={{ x: 5 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    {...(tourId ? { 'data-tour': tourId } : {})}
                  >
                    <span style={{ fontWeight: 500, color: isActive ? '#2ec291' : 'inherit', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{feature.text}</span>
                  </motion.div>
                </MenuItem>
              );
            }
          })}
        </Menu>
      </Sidebar>
    </aside>
  );
}
